import { useState, useEffect } from 'react';
import { getDetalleErroresEmpresa, createPreciosEmpresaMasivo } from '@shared/helpers/api/mantenedores/tarifarios';
import { getRegiones } from '@shared/helpers/api/mantenedores/generales';
import { SuccessSwal, ErrorSwal } from '@shared/helpers/swal';

/**
 * Hook para manejar el detalle de errores de empresa
 */
export const useTarifariosErroresEmpresaMantenedor = (user, permisos) => {
    const [detalleErrores, setDetalleErrores] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [savingPrecios, setSavingPrecios] = useState(false);

    // Filtros
    const [regionId, setRegionId] = useState('');
    const [regiones, setRegiones] = useState([]);

    // Cargar regiones para filtro
    useEffect(() => {
        const loadRegiones = async () => {
            if (!user) return;

            try {
                const response = await getRegiones(user);
                setRegiones(response.success ? response.data : []);
            } catch (error) {
                console.error('Error al cargar regiones:', error);
                setRegiones([]);
            }
        };

        loadRegiones();
    }, [user]);

    // Cargar detalle de errores de empresa
    useEffect(() => {
        const loadDetalleErrores = async () => {
            if (!user) return;

            setLoading(true);
            setError(null);

            try {
                const params = {
                    region_id: regionId || undefined
                };

                const response = await getDetalleErroresEmpresa(user, params);

                if (response.success) {
                    setDetalleErrores(response.data);
                } else {
                    setDetalleErrores(null);
                    setError(response.error);
                }
            } catch (error) {
                console.error('Error al cargar detalle de errores de empresa:', error);
                setDetalleErrores(null);
                setError('Error al cargar detalle de errores de empresa');
            } finally {
                setLoading(false);
            }
        };

        loadDetalleErrores();
    }, [user, regionId]);

    const handleRegionChange = (value) => {
        setRegionId(value);
    };

    const handleSavePrecios = async (erroresConPrecios) => {
        if (!user || !erroresConPrecios || erroresConPrecios.length === 0) return;

        setSavingPrecios(true);

        try {
            // Preparar datos para precios de empresa
            const preciosEmpresa = erroresConPrecios.map(error => ({
                empresa_id: error.empresa_id,
                servicio_spread_id: error.servicio_spread_id,
                comuna_id: error.comuna_id,
                precio: error.precio,
                activo: true
            }));

            const response = await createPreciosEmpresaMasivo(user, preciosEmpresa);

            if (response.success) {
                const { total_creados, total_errores } = response.data;
                SuccessSwal(`Precios de empresa creados exitosamente. ${total_creados} creados${total_errores > 0 ? `, ${total_errores} errores` : ''}`);
                
                // Recargar datos
                const params = {
                    region_id: regionId || undefined
                };
                const nuevosErrores = await getDetalleErroresEmpresa(user, params);
                if (nuevosErrores.success) {
                    setDetalleErrores(nuevosErrores.data);
                }
            } else {
                ErrorSwal(response.error || 'Error al crear precios de empresa');
            }
        } catch (error) {
            console.error('Error al guardar precios de empresa:', error);
            ErrorSwal('Error al guardar precios de empresa');
        } finally {
            setSavingPrecios(false);
        }
    };

    return {
        detalleErrores,
        loading,
        error,
        regionId,
        regiones,
        handleRegionChange,
        handleSavePrecios,
        savingPrecios
    };
};
